import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Popover,
  Box,
  Typography,
  TextField,
  Grid,
  Dialog,
  Checkbox,
  Menu,
  Divider,
  MenuItem,
  Chip,
  Collapse,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import { Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Close as CloseIcon } from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import {
  Edit,
  Delete as DeleteIcon,
  Print,
  Visibility,
  Description as FileIcon,
  AttachFile as AttachFileIcon,
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  SalesOrderListApi,
  SalesOrderDelete,
  ApproveSo,
  UpdateSalesOrderComplete,
} from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";
import { toast, ToastContainer } from "react-toastify";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import config from "../../../config";
import { generatePdf } from "./SalesOrderPrint";
import { SalesListExport, getCompanyProfile } from "../../../Services/Services";
import TimelineIcon from "@mui/icons-material/Timeline";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import MOSPhaseModal from "./MOSPhaseStatus";
import MOSPhaseOverviewModal from "./MOSPhaseOverviewModal";

pdfMake.vfs = pdfFonts.vfs;

// ── SalesOrderPreview Component ──
const SalesOrderPreview = ({ order, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const stored = localStorage.getItem("selectedDivision");
  const divisionLocalStorage = stored ? JSON.parse(stored) : null;

  // State to store dynamic company profile data
  const [companyProfile, setCompanyProfile] = useState(null);

  // Fetch company profile on component mount
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const data = await getCompanyProfile();
        setCompanyProfile(data);
      } catch (error) {
        console.error("Error fetching company profile in preview:", error);
      }
    };
    fetchCompanyData();
  }, []);

  const formatCurrency = (amount) => {
    return amount
      ? Number(amount).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";
  };

  const borderStyle = "1px solid #000";

  const calculateSubtotal = () => {
    if (!order.items || order.items.length === 0) return 0;
    return order.items.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: isMobile ? "100%" : isTablet ? "700px" : "900px",
        border: borderStyle,
        fontFamily: "Arial, sans-serif",
        bgcolor: "#fff",
        fontSize: isMobile ? "10px" : "12px",
        color: "#000",
        mx: "auto",
        position: "relative",
      }}
    >
      {/* Title */}
      <Typography
        variant="subtitle1"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          borderBottom: borderStyle,
          py: isMobile ? 0.3 : 0.5,
          fontSize: isMobile ? "0.9rem" : "1rem",
          color: "#000",
        }}
      >
        MOS
      </Typography>

      {/* Header Info Grid */}
      <Grid container sx={{ borderBottom: borderStyle }}>
        {/* Supplier (Dynamic Company Profile Details) */}
        <Grid
          item
          xs={12}
          sm={4}
          sx={{
            p: isMobile ? 0.8 : 1,
            borderRight: { xs: "none", sm: borderStyle },
            borderBottom: { xs: borderStyle, sm: "none" },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              fontSize: isMobile ? "0.65rem" : "0.75rem",
              color: "#000",
            }}
          >
            Supplier:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              color: "#000",
            }}
          >
            {companyProfile?.company_name || "SIDDHART INDUSTRIES"}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: isMobile ? "0.6rem" : "0.7rem",
              lineHeight: 1.3,
              color: "#000",
            }}
          >
            {companyProfile?.address ||
              "Plot No.-37, Phase-2, Siltara Industrial Area, Raipur - 493111"}
            <br />
            Email: {companyProfile?.email || "siddharthindustries@gmail.com"}
            <br />
            GSTIN:{" "}
            {companyProfile?.gstin ||
              divisionLocalStorage?.gst_no ||
              "22AAHFK2314C1Z4"}
            <br />
            Contact: {companyProfile?.phone || "9825586999"}
          </Typography>
        </Grid>

        {/* Project/SO Info - ✅ Includes Priority, Voltage, Rating & Winding */}
        <Grid
          item
          xs={12}
          sm={4}
          sx={{
            p: isMobile ? 0.8 : 1,
            borderRight: { xs: "none", sm: borderStyle },
            borderBottom: { xs: borderStyle, sm: "none" },
          }}
        >
          {[
            { label: "MOS:", value: order.project_name || "N/A" },
            { label: "MOS No.:", value: order.so_no },
            { label: "Priority:", value: order.priority || "Normal" },
            {
              label: "Rating Spec:",
              value: order.rating_specification || "N/A",
            },
            { label: "Voltage Ratio:", value: order.voltage_ratio || "N/A" },
            { label: "Winding:", value: order.winding_material || "N/A" },
            {
              label: "MOS Date:",
              value: new Date(order.order_date).toLocaleDateString("en-GB"),
            },
            {
              label: "Delivery Date:",
              value: order.delivery_date
                ? new Date(order.delivery_date).toLocaleDateString("en-GB")
                : "N/A",
            },
            { label: "P.O. No.:", value: order.po_number },
            {
              label: "P.O. Date:",
              value: order.order_date
                ? new Date(order.order_date).toLocaleDateString("en-GB")
                : "N/A",
            },
          ].map(({ label, value }) => (
            <Box
              key={label}
              sx={{ display: "flex", gap: 0.5, mb: 0.2, flexWrap: "wrap" }}
            >
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{
                  fontSize: isMobile ? "0.62rem" : "0.72rem",
                  color: "#000",
                }}
              >
                {label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: isMobile ? "0.6rem" : "0.7rem",
                  wordBreak: "break-word",
                  color: "#000",
                }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Grid>

        {/* Buyer */}
        <Grid item xs={12} sm={4} sx={{ p: isMobile ? 0.8 : 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              fontSize: isMobile ? "0.65rem" : "0.75rem",
              color: "#000",
            }}
          >
            Buyer:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              color: "#000",
            }}
          >
            {order.party_details?.company_name || "N/A"}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: isMobile ? "0.6rem" : "0.7rem",
              lineHeight: 1.3,
              color: "#000",
            }}
          >
            Contact: {order.party_details?.contact || "N/A"}
            <br />
            GST: {order.party_details?.gst || "N/A"}
            <br />
            Email: {order.party_details?.email || "N/A"}
            <br />
            Address: {order.party_details?.address || "N/A"}
          </Typography>
        </Grid>
      </Grid>

      {/* Items Table */}
      <Box sx={{ borderBottom: borderStyle, overflowX: "auto" }}>
        {isMobile ? (
          <Box>
            {(order.items?.length > 0 ? order.items : []).map((item, index) => (
              <Box
                key={item.id || index}
                sx={{
                  p: 1,
                  borderBottom: borderStyle,
                  bgcolor: index % 2 === 0 ? "#fafafa" : "#fff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.8,
                    pb: 0.5,
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{ fontSize: "0.75rem", color: "#1976d2" }}
                  >
                    {index + 1}. {item.item_name || "N/A"}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{ fontSize: "0.75rem", color: "#2e7d32" }}
                  >
                    ₹{formatCurrency(item.amount)}
                  </Typography>
                </Box>

                {item.description && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      color: "#666",
                      display: "block",
                      mb: 0.8,
                      fontStyle: "italic",
                      pb: 0.5,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    {item.description}
                  </Typography>
                )}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  {[
                    {
                      label: "Category:",
                      value: item.category_name || "-",
                    },
                    {
                      label: "Product Type:",
                      value: item.size_name || "-",
                    },
                    {
                      label: "Quantity:",
                      value: `${item.quantity || 0} ${item.uom || "Nos"}`,
                    },
                    { label: "Rate:", value: `₹${formatCurrency(item.rate)}` },
                    { label: "Discount:", value: `${item.dis_per || 0}%` },
                    {
                      label: "Base Amount:",
                      value: `₹${formatCurrency(
                        Number(item.quantity || 0) * Number(item.rate || 0),
                      )}`,
                    },
                  ].map(({ label, value }) => (
                    <Box
                      key={label}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        pb: 0.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.65rem", fontWeight: 500 }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.65rem" }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Table
            size="small"
            sx={{
              borderCollapse: "collapse",
              "& td, & th": {
                border: borderStyle,
                borderRight: borderStyle,
                borderBottom: borderStyle,
                color: "#000",
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ height: "30px" }}>
                {[
                  "S.No.",
                  "Category",
                  "Product Type",
                  "Item Description",
                  "UOM",
                  "Qty",
                  "Rate",
                  "Disc%",
                  "Amount",
                  "Total",
                ].map((h, i) => (
                  <TableCell
                    key={h}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      padding: "4px",
                      fontSize: isTablet ? "0.65rem" : "0.75rem",
                      bgcolor: "#f5f5f5",
                      width: h === "Item Description" ? "25%" : "auto", // width handle
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(order.items?.length > 0 ? order.items : []).map(
                (item, index) => (
                  <TableRow key={item.id || index} sx={{ height: "30px" }}>
                    <TableCell
                      align="center"
                      sx={{ padding: "4px", fontSize: "0.7rem" }}
                    >
                      {index + 1}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ padding: "4px", fontSize: "0.7rem" }}
                    >
                      {item.category_name || "-"}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ padding: "4px", fontSize: "0.7rem" }}
                    >
                      {item.size_name || "-"}
                    </TableCell>
                    <TableCell sx={{ padding: "4px" }}>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ fontSize: "0.7rem" }}
                      >
                        {item.item_name || "N/A"}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ fontSize: "0.65rem" }}
                      >
                        {item.description || ""}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ padding: "4px", fontSize: "0.7rem" }}
                    >
                      {item.uom || "Nos"}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ padding: "4px", fontSize: "0.7rem" }}
                    >
                      {item.quantity || 0}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ padding: "4px", fontSize: "0.7rem" }}
                    >
                      {formatCurrency(item.rate)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ padding: "4px", fontSize: "0.7rem" }}
                    >
                      {item.dis_per || 0}%
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ padding: "4px", fontSize: "0.7rem" }}
                    >
                      {formatCurrency(
                        Number(item.quantity || 0) * Number(item.rate || 0),
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ padding: "4px", fontSize: "0.7rem" }}
                    >
                      {formatCurrency(item.amount)}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        )}
      </Box>

      {/* Footer & Totals */}
      <Grid container>
        {/* Terms */}
        <Grid
          item
          xs={12}
          sm={8}
          sx={{
            p: isMobile ? 1.5 : 2,
            borderRight: { xs: "none", sm: borderStyle },
            borderBottom: { xs: borderStyle, sm: "none" },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              mb: 1,
              fontSize: isMobile ? "0.7rem" : "0.75rem",
              color: "#000",
            }}
          >
            Terms & Conditions:
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {order.terms_and_conditions?.map((term, index) => (
              <Typography
                key={index}
                variant="caption"
                sx={{
                  fontSize: isMobile ? "0.6rem" : "10px",
                  lineHeight: 1.4,
                  color: "#000",
                }}
              >
                <strong>{term.key}:</strong> {term.value}
              </Typography>
            ))}
          </Box>
          {order.remarks && (
            <Typography
              variant="caption"
              sx={{
                mt: 1,
                fontStyle: "italic",
                fontSize: isMobile ? "0.65rem" : "0.7rem",
                color: "#000",
              }}
            >
              <strong>Remarks:</strong> {order.remarks}
            </Typography>
          )}
        </Grid>

        {/* Totals */}
        <Grid item xs={12} sm={4}>
          {[
            {
              label: "Subtotal (Rs.)",
              value: formatCurrency(calculateSubtotal()),
            },
            {
              label: `CGST (${order.cgst_per || 0}%)`,
              value: formatCurrency(
                (calculateSubtotal() * Number(order.cgst_per || 0)) / 100,
              ),
            },
            {
              label: `SGST (${order.sgst_per || 0}%)`,
              value: formatCurrency(
                (calculateSubtotal() * Number(order.sgst_per || 0)) / 100,
              ),
            },
            {
              label: "Round Off",
              value: formatCurrency(order.round_off),
            },
          ].map(({ label, value }) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                p: isMobile ? 0.8 : 0.5,
                borderBottom: borderStyle,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: isMobile ? "0.65rem" : "0.7rem",
                  color: "#000",
                }}
              >
                {label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: isMobile ? "0.65rem" : "0.7rem",
                  color: "#000",
                }}
              >
                {value}
              </Typography>
            </Box>
          ))}

          {/* Grand Total */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              p: isMobile ? 0.8 : 0.5,
              bgcolor: "#f0f0f0",
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{
                fontSize: isMobile ? "0.75rem" : "0.875rem",
                color: "#000",
              }}
            >
              Total (Rs.)
            </Typography>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{
                fontSize: isMobile ? "0.75rem" : "0.875rem",
                color: "#000",
              }}
            >
              {formatCurrency(order.grand_total)}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// ── Main Component ──
export default function SalesOrderTable() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const tableHeadBg = isDark ? "#252b38" : "#f0f4ff";

  const rowBg = (index) =>
    isDark
      ? index % 2 === 0
        ? "#20242e"
        : "#1a1d26"
      : index % 2 === 0
        ? "#fafafa"
        : "#ffffff";

  const rowHoverBg = isDark ? "#2c3242" : "#e3f2fd";

  const COL_DELIVERED = { left: 0, width: 70 };
  const COL_SNO = { left: 70, width: 60 };
  const COL_SONO = { left: 130, width: 150 }; // ✅ Width 150px kar di taaki lamba number bhi poora aaye
  const COL_DEBTOR = { left: 280, width: 170 }; // ✅ Left offset 280 kar diya
  const ACTION_MIN_WIDTH = 190;

  const getStatusBorderColor = (status) => {
    if (status === "Approved") return "#4caf50";
    if (status === "Rejected") return "#f44336";
    return "#ff9800";
  };

  // ✅ 100% VISIBLE PRIORITY CHIP (Dark & Light Mode Solid Colors)
  const renderPriorityChip = (priority) => {
    const p = (priority || "Normal").toString().trim().toLowerCase();

    // 🔴 HIGH PRIORITY — Solid Red
    if (p === "high") {
      return (
        <Chip
          label="High"
          size="small"
          sx={{
            height: 22,
            fontSize: "0.68rem",
            fontWeight: 700,
            backgroundColor: "#d32f2f !important",
            color: "#ffffff !important",
            borderRadius: "4px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
            "& .MuiChip-label": {
              px: 1,
              color: "#ffffff !important",
            },
          }}
        />
      );
    }

    // 🟡 LOW PRIORITY — Solid Amber Yellow
    if (p === "low") {
      return (
        <Chip
          label="Low"
          size="small"
          sx={{
            height: 22,
            fontSize: "0.68rem",
            fontWeight: 700,
            backgroundColor: "#f59e0b !important",
            color: "#ffffff !important",
            borderRadius: "4px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
            "& .MuiChip-label": {
              px: 1,
              color: "#ffffff !important",
            },
          }}
        />
      );
    }

    // ⚪ NORMAL PRIORITY — Solid Grey
    return (
      <Chip
        label="Normal"
        size="small"
        sx={{
          height: 22,
          fontSize: "0.68rem",
          fontWeight: 600,
          backgroundColor: isDark ? "#334155 !important" : "#e2e8f0 !important",
          color: isDark ? "#f8fafc !important" : "#334155 !important",
          borderRadius: "4px",
          border: `1px solid ${isDark ? "#475569" : "#cbd5e1"}`,
          "& .MuiChip-label": {
            px: 1,
            color: isDark ? "#f8fafc !important" : "#334155 !important",
          },
        }}
      />
    );
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [salesOrders, setSalesOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [approveAnchorEl, setApproveAnchorEl] = useState(null);
  const baseURL = config.staticBaseURL;
  const [soToApprove, setSoToApprove] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const userRole = localStorage.getItem("role");
  const [expandedSO, setExpandedSO] = useState(null);
  const [isExcelLoading, setExcelLoading] = useState(false);

  // Phase Overview Modal State
  const [phaseOverviewOpen, setPhaseOverviewOpen] = useState(false);

  const [filters, setFilters] = useState({
    project_name: "",
    party: "",
    date_range: "this_month",
    po_date_from: "",
    po_date_to: "",
  });

  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({
    from: "",
    to: "",
  });
  const [poDateMenuAnchor, setPoDateMenuAnchor] = useState(null);
  const [poDateRange, setPoDateRange] = useState({ from: "", to: "" });
  const [tempPoDateRange, setTempPoDateRange] = useState({
    from: "",
    to: "",
  });
  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });

  const [phaseModalOpen, setPhaseModalOpen] = useState(false);
  const [selectedMOS, setSelectedMOS] = useState(null);
  const [deliverAnchorEl, setDeliverAnchorEl] = useState(null);
  const [soToDeliver, setSoToDeliver] = useState(null);
  const [deliverNextValue, setDeliverNextValue] = useState(false);

  const handleOpenPhaseModal = (mos) => {
    setSelectedMOS(mos);
    setPhaseModalOpen(true);
  };

  const handleClosePhaseModal = () => {
    setPhaseModalOpen(false);
    setSelectedMOS(null);
  };

  const handlePhaseUpdated = (phaseId, updatedData) => {
    setSalesOrders((prevList) =>
      prevList.map((so) => {
        if (so.id !== selectedMOS?.id) return so;
        return {
          ...so,
          mos_phases: so.mos_phases.map((p) =>
            p.id === phaseId ? { ...p, ...updatedData } : p,
          ),
        };
      }),
    );
    setSelectedMOS((prev) =>
      prev
        ? {
            ...prev,
            mos_phases: prev.mos_phases.map((p) =>
              p.id === phaseId ? { ...p, ...updatedData } : p,
            ),
          }
        : null,
    );
  };

  const handleExcelExport = async () => {
    if (salesOrders.length === 0) {
      toast.warning("No data available to export");
      return;
    }
    setExcelLoading(true);
    try {
      const params = { ...filters };
      if (
        filters.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        params.from_date = customDateRange.from;
        params.to_date = customDateRange.to;
      }
      if (filters.po_date_from && filters.po_date_to) {
        params.po_date_from = filters.po_date_from;
        params.po_date_to = filters.po_date_to;
      }
      const blob = await SalesListExport(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Sales_Orders_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchOrders(1, true, filters);
    }, 500);
    return () => clearTimeout(handler);
  }, [filters, customDateRange, poDateRange]);

  const fetchOrders = async (
    pageNum = 1,
    reset = false,
    filterValues = filters,
  ) => {
    try {
      if (reset) setFilterLoading(true);
      else setLoading(true);

      const params = { ...filterValues };
      if (
        filterValues.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        params.from_date = customDateRange.from;
        params.to_date = customDateRange.to;
      }
      if (filterValues.po_date_from && filterValues.po_date_to) {
        params.po_date_from = filterValues.po_date_from;
        params.po_date_to = filterValues.po_date_to;
      }

      const data = await SalesOrderListApi(pageNum, params);

      if (reset || pageNum === 1) {
        setSalesOrders(data.results);
      } else {
        setSalesOrders((prev) => [...prev, ...data.results]);
      }
      setHasMore(data.next !== null);
      setPage(pageNum);
    } catch (error) {
      console.error("Failed to fetch mos:", error);
      toast.error("Failed to load mos");
    } finally {
      setFilterLoading(false);
      setLoading(false);
    }
  };

  const fetchMoreData = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchOrders(nextPage);
  };

  const handleClick = () => navigate("/dashboard/saleorder");

  const handleReset = () => {
    setFilters({
      project_name: "",
      party: "",
      date_range: "this_month",
      po_date_from: "",
      po_date_to: "",
    });
    setCustomDateRange({ from: "", to: "" });
    setSalesOrders([]);
    setPage(1);
    setHasMore(true);
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
    setPoDateRange({ from: "", to: "" });
    setTempPoDateRange({ from: "", to: "" });
  };

  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  const openDeliverConfirm = (e, order) => {
    e.preventDefault();
    e.stopPropagation();
    setDeliverAnchorEl(e.currentTarget);
    setSoToDeliver(order);
    setDeliverNextValue(!order.is_complete);
  };

  const confirmDeliver = async () => {
    if (!soToDeliver) return;
    const order = soToDeliver;
    const checked = deliverNextValue;
    const prev = order.is_complete;
    setSalesOrders((list) =>
      list.map((o) => (o.id === order.id ? { ...o, is_complete: checked } : o)),
    );
    setDeliverAnchorEl(null);
    setSoToDeliver(null);
    try {
      const res = await UpdateSalesOrderComplete(order.id, checked);
      if (checked) {
        if (res?.sales_voucher?.created) {
          toast.success(
            `Dispatched! Draft Sales Voucher #${res.sales_voucher.sales_number} created in Accounts.`,
            { autoClose: 4500 },
          );
        } else if (res?.sales_voucher) {
          toast.success(
            `Dispatched! Linked to Sales Voucher #${res.sales_voucher.sales_number}.`,
          );
        } else {
          toast.success("Marked as dispatched / delivered.");
        }
      } else {
        toast.info("Marked as pending dispatch.");
      }
    } catch (err) {
      setSalesOrders((list) =>
        list.map((o) => (o.id === order.id ? { ...o, is_complete: prev } : o)),
      );
      toast.error("Delivery status save nahi hua");
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await SalesOrderDelete(id);
      if (response) {
        setSalesOrders((prev) => prev.filter((s) => s.id !== id));
        toast.success("MOS deleted successfully");
      } else {
        console.warn("Failed to delete from API");
      }
    } catch (error) {
      console.error("Error deleting MOS:", error);
      toast.error("Failed to delete MOS");
    }
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  function formatOnlyDate(dateTimeString, format = "dd-mm-yyyy") {
    const date = new Date(dateTimeString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    if (format === "dd-mm-yyyy") return `${day}-${month}-${year}`;
    if (format === "yyyy-mm-dd") return `${year}-${month}-${day}`;
    throw new Error("Unsupported format.");
  }

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };
    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  const handleApplyPoDateRange = () => {
    setPoDateRange(tempPoDateRange);
    setFilters((prev) => ({
      ...prev,
      po_date_from: tempPoDateRange.from,
      po_date_to: tempPoDateRange.to,
      date_range: "all",
    }));
    setPoDateMenuAnchor(null);
  };

  const handleCancelPoDateRange = () => {
    setTempPoDateRange(poDateRange);
    setPoDateMenuAnchor(null);
  };

  const handleClearPoDateRange = () => {
    setPoDateRange({ from: "", to: "" });
    setTempPoDateRange({ from: "", to: "" });
    setFilters((prev) => ({
      ...prev,
      po_date_from: "",
      po_date_to: "",
    }));
    setPoDateMenuAnchor(null);
  };

  // ── MOBILE CARD ──
  const MobileSalesOrderCard = ({ order, index }) => {
    const isExpanded = expandedSO === order.id;

    return (
      <Box key={order.id}>
        <Box
          onClick={() =>
            setExpandedSO(expandedSO === order.id ? null : order.id)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: rowBg(index),
            "&:active": { bgcolor: "action.selected" },
            borderLeft: `4px solid ${getStatusBorderColor(order.status)}`,
            mt: index === 0 ? { xs: 4, sm: 2 } : 0,
            transition: "background-color 0.15s ease",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "text.disabled",
                  minWidth: 25,
                }}
              >
                #{index + 1}
              </Typography>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                  flex: 1,
                  color: "text.primary",
                }}
              >
                {order.so_no}
              </Typography>
              {/* Mobile Priority Display */}
              {renderPriorityChip(order.priority)}
            </Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {order.party_details?.company_name?.substring(0, 20) || "N/A"}
              </span>
              <span>•</span>
              <span style={{ fontSize: "0.7rem" }}>
                {new Date(order.order_date).toLocaleDateString("en-GB")}
              </span>
            </Typography>
          </Box>

          <IconButton size="small" sx={{ ml: 1 }}>
            {expandedSO === order.id ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Collapse in={expandedSO === order.id} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? alpha(t.palette.common.white, 0.05)
                  : alpha(t.palette.primary.main, 0.04),
              borderLeft: `4px solid ${getStatusBorderColor(order.status)}`,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0.8,
                mb: 1,
              }}
            >
              {/* Status */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  STATUS
                </Typography>
                <Chip
                  label={order.status}
                  color={
                    order.status === "Approved"
                      ? "success"
                      : order.status === "Rejected"
                        ? "error"
                        : "warning"
                  }
                  size="small"
                  sx={{ height: 20, fontSize: "0.65rem", mt: 0.3 }}
                />
              </Box>

              {/* Priority */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  PRIORITY
                </Typography>
                <Box sx={{ mt: 0.3 }}>{renderPriorityChip(order.priority)}</Box>
              </Box>

              {/* Rating Specification */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  RATING SPEC
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "text.primary",
                  }}
                >
                  {order.rating_specification || "—"}
                </Typography>
              </Box>

              {/* Voltage Ratio */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  VOLTAGE RATIO
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "text.primary",
                  }}
                >
                  {order.voltage_ratio || "—"}
                </Typography>
              </Box>

              {/* Winding Material */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  WINDING
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "text.primary",
                  }}
                >
                  {order.winding_material || "—"}
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  WORK TYPE
                </Typography>
                <Box sx={{ mt: 0.3 }}>
                  {order.work_type_name ? (
                    <Chip
                      label={order.work_type_name}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        bgcolor: isDark
                          ? alpha(theme.palette.info.main, 0.18)
                          : "#e0f2fe",
                        color: isDark ? "#7dd3fc" : "#0369a1",
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{ fontSize: "0.72rem", color: "text.disabled" }}
                    >
                      —
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Delivered */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  DELIVERED
                </Typography>
                <Checkbox
                  size="small"
                  checked={!!order.is_complete}
                  onClick={(e) => openDeliverConfirm(e, order)}
                  sx={{ p: 0 }}
                />
              </Box>

              {/* Grand Total */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  GRAND TOTAL
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "success.main",
                  }}
                >
                  ₹{" "}
                  {order.grand_total
                    ? Number(order.grand_total).toLocaleString("en-IN", {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      })
                    : "0.0"}
                </Typography>
              </Box>

              {/* Received */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  RECEIVED
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "primary.main",
                  }}
                >
                  ₹{" "}
                  {order.rec_amount
                    ? Number(order.rec_amount).toLocaleString("en-IN", {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      })
                    : "0.0"}
                </Typography>
              </Box>

              {/* Balance */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  BALANCE
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "error.main",
                  }}
                >
                  ₹{" "}
                  {(
                    Number(order.grand_total) - Number(order.rec_amount)
                  ).toFixed(1)}
                </Typography>
              </Box>

              {/* Created By */}
              {order.created_by_name && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.6rem",
                      color: "text.disabled",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      fontWeight: 600,
                    }}
                  >
                    CREATED BY
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 500,
                      color: "text.secondary",
                    }}
                  >
                    {order.created_by_name}
                  </Typography>
                </Box>
              )}

              {/* Approved By */}
              {order.approved_name && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.6rem",
                      color: "text.disabled",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      fontWeight: 600,
                    }}
                  >
                    APPROVED BY
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 500,
                      color: "text.secondary",
                    }}
                  >
                    {order.approved_name}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* PO Number */}
            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "text.disabled",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  fontWeight: 600,
                  mb: 0.3,
                }}
              >
                PO NUMBER
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "text.secondary",
                  lineHeight: 1.3,
                }}
              >
                {order.po_number}
              </Typography>
            </Box>

            {/* Delivery Date */}
            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "text.disabled",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  fontWeight: 600,
                  mb: 0.3,
                }}
              >
                DELIVERY DATE
              </Typography>
              <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                {order.delivery_date
                  ? new Date(order.delivery_date).toLocaleDateString("en-GB")
                  : "—"}
              </Typography>
            </Box>

            {/* Project */}
            {order.project_name && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                    mb: 0.3,
                  }}
                >
                  PROJECT
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    lineHeight: 1.3,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {order.project_name}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1, borderColor: "divider" }} />

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              {userRole != "Staff" && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setApproveAnchorEl(true);
                    setSoToApprove(order);
                  }}
                  sx={{
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha(t.palette.primary.main, 0.15)
                        : "#e3f2fd",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(t.palette.primary.main, 0.28)
                          : "#bbdefb",
                    },
                  }}
                >
                  <Visibility
                    sx={{ fontSize: "1rem", color: "primary.main" }}
                  />
                </IconButton>
              )}

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 0.8,
                  mt: 1,
                  pt: 1,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                {order.project && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`${baseURL}${order.project}`, "_blank");
                    }}
                    sx={{
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(t.palette.primary.main, 0.15)
                          : "#e3f2fd",
                      borderRadius: "6px",
                      width: 36,
                      height: 36,
                      "&:hover": {
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.primary.main, 0.28)
                            : "#bbdefb",
                      },
                    }}
                  >
                    <AttachFileIcon
                      sx={{ fontSize: "1.1rem", color: "primary.main" }}
                    />
                  </IconButton>
                )}

                {/* Edit */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/dashboard/saleorder/${order.id}`);
                  }}
                  sx={{
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha(t.palette.success.main, 0.15)
                        : "#e8f5e9",
                    borderRadius: "6px",
                    width: 36,
                    height: 36,
                    "&:hover": {
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(t.palette.success.main, 0.28)
                          : "#c8e6c9",
                    },
                  }}
                >
                  <Edit sx={{ fontSize: "1.1rem", color: "success.main" }} />
                </IconButton>

                {/* Delete */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                    setOrderToDelete(order.id);
                  }}
                  sx={{
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha(t.palette.error.main, 0.15)
                        : "#ffebee",
                    borderRadius: "6px",
                    width: 36,
                    height: 36,
                    "&:hover": {
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(t.palette.error.main, 0.28)
                          : "#ffcdd2",
                    },
                  }}
                >
                  <DeleteIcon
                    sx={{ fontSize: "1.1rem", color: "error.main" }}
                  />
                </IconButton>

                {/* Print */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    generatePdf(order);
                  }}
                  sx={{
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha("#ff9800", 0.15)
                        : "#fff3e0",
                    borderRadius: "6px",
                    width: 36,
                    height: 36,
                    "&:hover": {
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha("#ff9800", 0.28)
                          : "#ffe0b2",
                    },
                  }}
                >
                  <Print sx={{ fontSize: "1.1rem", color: "#ff9800" }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Collapse>

        <Divider />
      </Box>
    );
  };

  return (
    <>
      {/* ── FILTERS BAR ── */}
      <Box
        sx={{
          mb: 1,
          mt: { xs: 4, md: 1 },
          position: "sticky",
          top: 0,
          zIndex: 1100,
          pt: 1,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
          backdropFilter: "blur(8px)",
          boxShadow: isDark
            ? "0 2px 10px rgba(0,0,0,0.45)"
            : "0 1px 4px rgba(0,0,0,0.07)",
        }}
      >
        <Grid container spacing={1} alignItems="center">
          {/* Date Range */}
          <Grid item xs={12} sm={6} md={1.8}>
            <TextField
              label="Date Range"
              value={
                filters.date_range === "custom" &&
                customDateRange.from &&
                customDateRange.to
                  ? `${formatDateDisplay(customDateRange.from)} to ${formatDateDisplay(customDateRange.to)}`
                  : filters.date_range === "today"
                    ? "Today"
                    : filters.date_range === "yesterday"
                      ? "Yesterday"
                      : filters.date_range === "this_week"
                        ? "This Week"
                        : filters.date_range === "previous_week"
                          ? "Previous Week"
                          : filters.date_range === "this_month"
                            ? "This Month"
                            : filters.date_range === "current_year"
                              ? "Current Year"
                              : filters.date_range === "financial_year"
                                ? "Financial Year"
                                : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                "& .MuiInputBase-input": {
                  fontSize: "0.8rem",
                  padding: "4px 8px",
                },
                "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.6rem" },
              }}
              fullWidth
            />
          </Grid>

          {/* Debtor */}
          <Grid item xs={12} sm={6} md={1.8}>
            <TextField
              label="Search By Debtor"
              name="party"
              value={filters.party}
              onChange={handleFilterChange}
              size="small"
              fullWidth
              sx={{
                "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.65rem" },
              }}
            />
          </Grid>

          {/* Project Name */}
          <Grid item xs={12} sm={6} md={1.8}>
            <TextField
              label="Search by MOS Name"
              name="project_name"
              value={filters.project_name}
              onChange={handleFilterChange}
              size="small"
              fullWidth
              sx={{
                "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.65rem" },
              }}
            />
          </Grid>

          {/* PO Date */}
          <Grid item xs={12} sm={6} md={1.8}>
            <TextField
              label="Po Date"
              value={
                poDateRange.from && poDateRange.to
                  ? `${formatDateDisplay(poDateRange.from)} to ${formatDateDisplay(poDateRange.to)}`
                  : poDateRange.from
                    ? `From ${formatDateDisplay(poDateRange.from)}`
                    : poDateRange.to
                      ? `To ${formatDateDisplay(poDateRange.to)}`
                      : ""
              }
              placeholder="Select Po Date Range"
              onClick={(e) => {
                setPoDateMenuAnchor(e.currentTarget);
                setTempPoDateRange(poDateRange);
              }}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                flex: "1 1 100px",
                cursor: "pointer",
                "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.65rem" },
                "& .MuiInputBase-input": { cursor: "pointer" },
              }}
            />
          </Grid>

          {/* PO Date Range Menu */}
          <Menu
            anchorEl={poDateMenuAnchor}
            open={Boolean(poDateMenuAnchor)}
            onClose={handleCancelPoDateRange}
            PaperProps={{
              sx: {
                width: 320,
                p: 0,
                bgcolor: "background.paper",
                backgroundImage: "none",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: isDark
                  ? "0 8px 28px rgba(0,0,0,0.6)"
                  : "0 4px 16px rgba(0,0,0,0.14)",
              },
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 2,
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.common.white, 0.04)
                    : "#fafafa",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
              >
                Select Po Date Range
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={tempPoDateRange.from}
                  onChange={(e) =>
                    setTempPoDateRange((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max:
                      tempPoDateRange.to ||
                      new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": { height: 40, fontSize: "0.75rem" },
                    "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                  }}
                />
                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={tempPoDateRange.to}
                  onChange={(e) =>
                    setTempPoDateRange((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: tempPoDateRange.from || undefined,
                    max: new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": { height: 40, fontSize: "0.75rem" },
                    "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                  }}
                />
              </Box>

              {tempPoDateRange.from && tempPoDateRange.to && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 2,
                    p: 1,
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha(t.palette.success.main, 0.15)
                        : "#e8f5e9",
                    borderRadius: 1,
                    textAlign: "center",
                    color: "success.main",
                    fontWeight: 500,
                  }}
                >
                  📅 {formatDateDisplay(tempPoDateRange.from)} to{" "}
                  {formatDateDisplay(tempPoDateRange.to)}
                </Typography>
              )}

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "space-between",
                }}
              >
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  onClick={handleClearPoDateRange}
                  disabled={!poDateRange.from && !poDateRange.to}
                  sx={{ fontSize: "0.7rem", textTransform: "none" }}
                >
                  Clear
                </Button>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelPoDateRange}
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      borderRadius: 1,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleApplyPoDateRange}
                    disabled={!tempPoDateRange.from || !tempPoDateRange.to}
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      borderRadius: 1,
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Box>
          </Menu>

          {/* Reset + Add + Excel + PHASE SUMMARY BUTTON */}
          <Grid item xs={12} sm={6} md={3.2}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                justifyContent: { xs: "space-between", sm: "flex-start" },
              }}
            >
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleReset}
                sx={{
                  height: 30,
                  fontSize: "0.7rem",
                  borderRadius: 0,
                  flex: { xs: 1, sm: "none" },
                  minWidth: { sm: 65 },
                }}
              >
                Reset
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={handleClick}
                startIcon={<AddIcon sx={{ fontSize: "0.9rem !important" }} />}
                sx={{
                  height: 30,
                  fontSize: "0.7rem",
                  borderRadius: 0,
                  flex: { xs: 1, sm: "none" },
                  minWidth: { sm: 100 },
                  whiteSpace: "nowrap",
                }}
              >
                MOS
              </Button>

              <Tooltip title="Export to Excel" arrow>
                <IconButton
                  onClick={handleExcelExport}
                  disabled={isExcelLoading || salesOrders.length === 0}
                  sx={{
                    height: 30,
                    width: 30,
                    minWidth: 30,
                    bgcolor: "success.main",
                    color: "white",
                    borderRadius: 0,
                    "&:hover": { bgcolor: "success.dark" },
                    "&:disabled": { bgcolor: "grey.400", color: "white" },
                  }}
                >
                  {isExcelLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <FileIcon sx={{ fontSize: "1rem" }} />
                  )}
                </IconButton>
              </Tooltip>

              <Tooltip title="MOS Phase Wise Summary & Tracking" arrow>
                <IconButton
                  onClick={() => setPhaseOverviewOpen(true)}
                  disabled={salesOrders.length === 0}
                  sx={{
                    height: 30,
                    width: 30,
                    minWidth: 30,
                    bgcolor: "primary.main",
                    color: "white",
                    borderRadius: 0,
                    "&:hover": { bgcolor: "primary.dark" },
                    "&:disabled": { bgcolor: "grey.400", color: "white" },
                  }}
                >
                  <ViewKanbanIcon sx={{ fontSize: "1.1rem" }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>

        {/* Date Range Menu */}
        <Menu
          anchorEl={dateMenuAnchor}
          open={Boolean(dateMenuAnchor)}
          onClose={() => {
            setDateMenuAnchor(null);
            setShowCustomDateInputs(false);
            setTempCustomDateRange({ from: "", to: "" });
          }}
          PaperProps={{
            sx: {
              width: 350,
              maxHeight: 500,
              bgcolor: "background.paper",
              backgroundImage: "none",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: isDark
                ? "0 8px 28px rgba(0,0,0,0.6)"
                : "0 4px 16px rgba(0,0,0,0.14)",
              "& .MuiTypography-root": { fontSize: "0.7rem" },
            },
          }}
        >
          {[
            { key: "today", label: "Today" },
            { key: "yesterday", label: "Yesterday" },
            { key: "this_week", label: "This Week" },
            { key: "previous_week", label: "Previous Week" },
            { key: "this_month", label: "This Month" },
            { key: "current_year", label: "Current Year" },
            { key: "financial_year", label: "Financial Year" },
          ].map((range) => (
            <MenuItem
              key={range.key}
              onClick={() => {
                setFilters((prev) => ({ ...prev, date_range: range.key }));
                setCustomDateRange({ from: "", to: "" });
                setDateMenuAnchor(null);
                setShowCustomDateInputs(false);
              }}
              sx={{
                "&:hover": {
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? alpha(t.palette.primary.main, 0.1)
                      : alpha(t.palette.primary.main, 0.06),
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="body2" sx={{ color: "text.primary" }}>
                  {range.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.65rem", color: "text.secondary" }}
                >
                  {getDateRangeDisplay(range.key)}
                </Typography>
              </Box>
            </MenuItem>
          ))}

          <Divider />

          <MenuItem
            onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: showCustomDateInputs
                ? (t) =>
                    t.palette.mode === "dark"
                      ? alpha(t.palette.primary.main, 0.15)
                      : "#e3f2fd"
                : "inherit",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: showCustomDateInputs ? 600 : 400,
                color: "text.primary",
              }}
            >
              Custom Date Range
            </Typography>
            {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
          </MenuItem>

          <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
            <Box
              sx={{
                px: 2,
                py: 2,
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.common.white, 0.04)
                    : "#f5f5f5",
                borderTop: "1px solid",
                borderColor: "divider",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography
                variant="caption"
                sx={{ mb: 1, display: "block", color: "text.secondary" }}
              >
                Select Date Range
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  value={tempCustomDateRange.from}
                  onChange={(e) =>
                    setTempCustomDateRange((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max:
                      tempCustomDateRange.to ||
                      new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": { height: 40, fontSize: "0.75rem" },
                    "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                  }}
                />
                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  value={tempCustomDateRange.to}
                  onChange={(e) =>
                    setTempCustomDateRange((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: tempCustomDateRange.from || undefined,
                    max: new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": { height: 40, fontSize: "0.75rem" },
                    "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                  }}
                />
              </Box>

              {tempCustomDateRange.from && tempCustomDateRange.to && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 1.5,
                    p: 1,
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha(t.palette.success.main, 0.15)
                        : "#e8f5e9",
                    borderRadius: 1,
                    textAlign: "center",
                    color: "success.main",
                    fontWeight: 500,
                  }}
                >
                  📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                  {formatDateDisplay(tempCustomDateRange.to)}
                </Typography>
              )}

              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelCustomDateRange}
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                    borderRadius: 1,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyCustomDateRange}
                  disabled={
                    !tempCustomDateRange.from || !tempCustomDateRange.to
                  }
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                    borderRadius: 1,
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Menu>
      </Box>

      {/* ── Main Content ── */}
      <Box sx={{ position: "relative" }}>
        {/* Filter Loading Overlay */}
        {filterLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: (t) =>
                t.palette.mode === "dark"
                  ? alpha(t.palette.background.paper, 0.9)
                  : "rgba(255, 255, 255, 0.9)",
              zIndex: 1000,
              borderRadius: 1,
            }}
          >
            <CircularProgress size={50} thickness={4} color="primary" />
            <Typography sx={{ mt: 2, color: "text.primary", fontWeight: 500 }}>
              Loading Data...
            </Typography>
            <Typography
              sx={{ mt: 0.5, color: "text.secondary", fontSize: "0.8rem" }}
            >
              Please wait, this may take a moment
            </Typography>
          </Box>
        )}

        <InfiniteScroll
          dataLength={salesOrders.length}
          next={fetchMoreData}
          hasMore={hasMore}
          scrollableTarget="main-scroll-container"
          loader={
            <Typography
              textAlign="center"
              p={2}
              fontSize="0.8rem"
              color="text.secondary"
            >
              Loading...
            </Typography>
          }
          endMessage={
            <Typography
              textAlign="center"
              p={2}
              fontSize="0.8rem"
              color="text.disabled"
            >
              No more records!
            </Typography>
          }
        >
          {isMobile ? (
            /* ── MOBILE ── */
            <Box sx={{ bgcolor: "background.paper" }}>
              {salesOrders.map((order, index) => (
                <MobileSalesOrderCard
                  key={order.id}
                  order={order}
                  index={index}
                />
              ))}
            </Box>
          ) : (
            /* ── DESKTOP TABLE ── */
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                overflow: "visible",
                bgcolor: "background.paper",
                backgroundImage: "none",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: "max-content",
                  "& td, & th": {
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableHead
                  sx={{
                    "& th": {
                      fontWeight: "bold",
                      fontSize: "0.7rem",
                      color: "text.primary",
                      backgroundColor: `${tableHeadBg} !important`,
                      borderBottom: "2px solid",
                      borderColor: "primary.main",
                      letterSpacing: "0.04em",
                    },
                  }}
                >
                  <TableRow>
                    {/* 1️⃣ Delivered — sticky left: 0 */}
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        left: COL_DELIVERED.left,
                        width: COL_DELIVERED.width,
                        minWidth: COL_DELIVERED.width,
                        backgroundColor: `${tableHeadBg} !important`,
                        zIndex: 3,
                      }}
                    >
                      Delivered
                    </TableCell>

                    {/* 2️⃣ S.No. — sticky left: 80 */}
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        left: COL_SNO.left,
                        width: COL_SNO.width,
                        minWidth: COL_SNO.width,
                        backgroundColor: `${tableHeadBg} !important`,
                        zIndex: 3,
                      }}
                    >
                      S.No.
                    </TableCell>

                    {/* 3️⃣ So. No. — sticky left: 150 */}
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        left: COL_SONO.left,
                        width: COL_SONO.width,
                        minWidth: COL_SONO.width,
                        backgroundColor: `${tableHeadBg} !important`,
                        zIndex: 3,
                      }}
                    >
                      MOS No.
                    </TableCell>

                    {/* 4️⃣ Debtor — sticky left: 260 */}
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        left: COL_DEBTOR.left,
                        width: COL_DEBTOR.width,
                        minWidth: COL_DEBTOR.width,
                        backgroundColor: `${tableHeadBg} !important`,
                        zIndex: 3,
                      }}
                    >
                      Debtor
                    </TableCell>

                    {/* ── Scrollable columns ── */}
                    {/* ✅ Priority */}
                    <TableCell align="center">Priority</TableCell>

                    {/* ✅ 3 Naye Columns */}
                    <TableCell align="center">Rating Spec</TableCell>
                    <TableCell align="center">Voltage Ratio</TableCell>
                    <TableCell align="center">Winding</TableCell>
                    <TableCell align="center">Work Type</TableCell>

                    <TableCell align="center">PO No</TableCell>
                    <TableCell align="center">PO Date</TableCell>
                    <TableCell align="center">Delivery Date</TableCell>
                    <TableCell align="center">Grand Total</TableCell>
                    <TableCell align="center">MOS</TableCell>
                    <TableCell align="center">Created By</TableCell>
                    <TableCell align="center">Date</TableCell>

                    {/* Action — sticky right */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        minWidth: ACTION_MIN_WIDTH,
                        backgroundColor: `${tableHeadBg} !important`,
                        zIndex: 3,
                      }}
                      align="center"
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {salesOrders.map((order, idx) => (
                    <TableRow
                      key={order.id || idx}
                      sx={{
                        backgroundColor: rowBg(idx),
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: `${rowHoverBg} !important`,
                          "& td": {
                            backgroundColor: "inherit !important",
                          },
                        },
                        "& td": {
                          paddingRight: "13px !important",
                          paddingLeft: "13px !important",
                          padding: "0px",
                          fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          whiteSpace: "nowrap",
                          color: "text.primary",
                        },
                      }}
                    >
                      {/* 1️⃣ Delivered — sticky left: 0 */}
                      <TableCell
                        align="center"
                        sx={{
                          position: "sticky",
                          left: COL_DELIVERED.left,
                          width: COL_DELIVERED.width,
                          minWidth: COL_DELIVERED.width,
                          bgcolor: "inherit",
                          zIndex: 2,
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={!!order.is_complete}
                          onClick={(e) => openDeliverConfirm(e, order)}
                          sx={{ p: 0 }}
                        />
                      </TableCell>

                      {/* 2️⃣ S.No. — sticky left: 80 */}
                      <TableCell
                        align="center"
                        sx={{
                          position: "sticky",
                          left: COL_SNO.left,
                          width: COL_SNO.width,
                          minWidth: COL_SNO.width,
                          bgcolor: "inherit",
                          zIndex: 2,
                        }}
                      >
                        {idx + 1}
                      </TableCell>

                      {/* 3️⃣ So. No. — sticky left: 150 */}
                      <TableCell
                        align="center"
                        sx={{
                          position: "sticky",
                          left: COL_SONO.left,
                          width: COL_SONO.width,
                          minWidth: COL_SONO.width,
                          bgcolor: "inherit",
                          zIndex: 2,
                          fontWeight: 600,
                          color: "primary.main",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {order.so_no}
                      </TableCell>

                      {/* 4️⃣ Debtor — sticky left: 260 */}
                      <TableCell
                        align="center"
                        sx={{
                          position: "sticky",
                          left: COL_DEBTOR.left,
                          width: COL_DEBTOR.width,
                          minWidth: COL_DEBTOR.width,
                          bgcolor: "inherit",
                          zIndex: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 134,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            mx: "auto",
                          }}
                        >
                          <Tooltip
                            title={order.party_details?.company_name}
                            arrow
                          >
                            <span>
                              {truncateText(
                                order.party_details?.company_name,
                                20,
                              )}
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      {/* ── Scrollable cells ── */}
                      {/* Priority */}
                      <TableCell align="center">
                        {renderPriorityChip(order.priority)}
                      </TableCell>

                      {/* ✅ 3 Naye Fields Data Display */}
                      <TableCell align="center">
                        <Tooltip
                          title={order.rating_specification || "—"}
                          arrow
                        >
                          <span>
                            {truncateText(order.rating_specification, 18) ||
                              "—"}
                          </span>
                        </Tooltip>
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip title={order.voltage_ratio || "—"} arrow>
                          <span>
                            {truncateText(order.voltage_ratio, 18) || "—"}
                          </span>
                        </Tooltip>
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip title={order.winding_material || "—"} arrow>
                          <span>
                            {truncateText(order.winding_material, 15) || "—"}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {order.work_type_name ? (
                          <Chip
                            label={order.work_type_name}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              borderRadius: "4px",
                              backgroundColor: isDark
                                ? alpha(theme.palette.info.main, 0.18)
                                : "#e0f2fe",
                              color: isDark ? "#7dd3fc" : "#0369a1",
                              border: `1px solid ${isDark ? alpha(theme.palette.info.main, 0.3) : "#bae6fd"}`,
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        ) : (
                          <Typography
                            sx={{ fontSize: "0.7rem", color: "text.disabled" }}
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip title={order.po_number} arrow>
                          <span>{truncateText(order.po_number, 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {new Date(order.order_date).toLocaleDateString("en-GB")}
                      </TableCell>
                      <TableCell align="center">
                        {order.delivery_date
                          ? new Date(order.delivery_date).toLocaleDateString(
                              "en-GB",
                            )
                          : "—"}
                      </TableCell>
                      <TableCell align="center">
                        {order.grand_total
                          ? Number(order.grand_total).toLocaleString("en-IN", {
                              minimumFractionDigits: 1,
                              maximumFractionDigits: 1,
                            })
                          : "0.00"}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={order.project_name} arrow>
                          <span>{truncateText(order.project_name, 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {order.created_by_name}
                      </TableCell>
                      <TableCell align="center">
                        {formatOnlyDate(order.created_at)}
                      </TableCell>

                      <TableCell
                        sx={{
                          position: "sticky",
                          right: 0,
                          minWidth: ACTION_MIN_WIDTH,
                          bgcolor: "inherit",
                          zIndex: 2,
                        }}
                        align="center"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.3,
                          }}
                        >
                          {order.project && (
                            <Tooltip title="View Attachment File" arrow>
                              <IconButton
                                sx={{ p: 0.3 }}
                                size="small"
                                color="info"
                                onClick={() =>
                                  window.open(`${order.project}`, "_blank")
                                }
                              >
                                <AttachFileIcon transform="scale(0.65)" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {userRole !== "Staff" && (
                            <Tooltip title="Preview / Approve" arrow>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setApproveAnchorEl(true);
                                  setSoToApprove(order);
                                }}
                                sx={{ p: 0.3 }}
                              >
                                <Visibility
                                  transform="scale(0.65)"
                                  sx={{ color: "primary.main" }}
                                />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Edit */}
                          <Tooltip title="Edit MOS" arrow>
                            <IconButton
                              sx={{ p: 0.3 }}
                              onClick={() =>
                                navigate(`/dashboard/saleorder/${order.id}`)
                              }
                              size="small"
                              color="primary"
                            >
                              <Edit transform="scale(0.65)" />
                            </IconButton>
                          </Tooltip>

                          {/* Delete */}
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              sx={{ p: 0.3 }}
                              color="error"
                              size="small"
                              onClick={(e) => {
                                setAnchorEl(e.currentTarget);
                                setOrderToDelete(order.id);
                              }}
                            >
                              <DeleteIcon transform="scale(0.65)" />
                            </IconButton>
                          </Tooltip>

                          {/* Print PDF */}
                          <Tooltip title="Print PDF" arrow>
                            <IconButton
                              sx={{ p: 0.3 }}
                              size="small"
                              color="default"
                              onClick={() => generatePdf(order)}
                            >
                              <Print transform="scale(0.65)" />
                            </IconButton>
                          </Tooltip>

                          {/* Phases Modal */}
                          <Tooltip title="View / Update Phases" arrow>
                            <IconButton
                              sx={{ p: 0.3 }}
                              size="small"
                              color="primary"
                              onClick={() => handleOpenPhaseModal(order)}
                            >
                              <TimelineIcon transform="scale(0.65)" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InfiniteScroll>
      </Box>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme={isDark ? "dark" : "light"}
      />

      {/* ── Delete Popover ── */}
      <Popover
        disableScrollLock
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setOrderToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 8px 28px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.14)",
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 220 }}>
          <Typography variant="body2" sx={{ color: "text.primary", mb: 1 }}>
            Are you sure you want to delete this MOS?
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAnchorEl(null);
                setOrderToDelete(null);
              }}
            >
              No
            </Button>
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={async () => {
                await handleDelete(orderToDelete);
                setAnchorEl(null);
                setOrderToDelete(null);
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* ── Deliver Confirm Popover ── */}
      <Popover
        disableScrollLock
        open={Boolean(deliverAnchorEl)}
        anchorEl={deliverAnchorEl}
        onClose={() => {
          setDeliverAnchorEl(null);
          setSoToDeliver(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 8px 28px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.14)",
          },
        }}
      >
        <Box sx={{ p: 2, maxWidth: 240 }}>
          <Typography variant="body2" sx={{ color: "text.primary" }}>
            {deliverNextValue
              ? "Is this delivered? Mark as complete?"
              : "Remove delivered status?"}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1.5,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setDeliverAnchorEl(null);
                setSoToDeliver(null);
              }}
            >
              No
            </Button>
            <Button
              color="success"
              variant="contained"
              size="small"
              onClick={confirmDeliver}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* ── Approve / Preview Dialog ── */}
      <Dialog
        open={Boolean(approveAnchorEl)}
        onClose={() => {
          setApproveAnchorEl(null);
          setSoToApprove(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            borderRadius: 2,
            width: { xs: "95vw", md: "900px" },
            maxWidth: "95vw",
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 12px 40px rgba(0,0,0,0.65)"
              : "0 8px 32px rgba(0,0,0,0.2)",
          },
        }}
      >
        {soToApprove && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                overflowY: "auto",
                p: 2,
                bgcolor: isDark
                  ? alpha(theme.palette.common.white, 0.03)
                  : "#f5f5f5",
                maxHeight: "calc(90vh - 100px)",
              }}
            >
              <SalesOrderPreview order={soToApprove} />
            </Box>

            <Box
              sx={{
                p: 2,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  color="error"
                  size="small"
                  onClick={() => {
                    setApproveAnchorEl(null);
                    setSoToApprove(null);
                  }}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  Close
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Dialog>

      {/* Individual MOS Phase Update Modal */}
      <MOSPhaseModal
        open={phaseModalOpen}
        onClose={handleClosePhaseModal}
        mos={selectedMOS}
        onPhaseUpdated={handlePhaseUpdated}
      />

      {/* Overall Phase-wise Tracking & Summary Modal */}
      <MOSPhaseOverviewModal
        open={phaseOverviewOpen}
        onClose={() => setPhaseOverviewOpen(false)}
        salesOrders={salesOrders}
        onOpenPhaseModal={(order) => {
          handleOpenPhaseModal(order);
        }}
      />
    </>
  );
}
